/**
 * 用途の段（イベント選択・選択肢・数値など）を 1 つずつ見せる。
 *
 * 選べるイベントと、イベントごとに選べる選択肢（例: 開催後なら連絡文は「お礼」だけ）は
 * サーバーが決めて返す（ChatFlowButton.eligibleEvents）。ここでは表示と入力だけをする。
 * イベントが決まっている（イベント画面から開いた・次の一手から来た）ときは、その段を飛ばす。
 */

import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronLeftIcon } from '@heroicons/react/24/outline';
import AiMark from '@/components/chat/AiMark';
import { DisabledReason } from '@/components/common/DisabledReason';
import { CHAT_STEP_KIND } from '@/constants/chat';
import { useAuth } from '@/hooks/useAuth';
import { chatQueryKeys } from '@/queryKeys/chat';
import {
  getChatComments,
  type ChatFlowButton,
  type ChatFlowStep,
  type ChatHomeEvent,
} from '@/services/chat';

export type FlowSlots = Record<string, string | string[]>;

interface ChatFlowStepsProps {
  flow: ChatFlowButton;
  events: Map<string, ChatHomeEvent>;
  initialSlots: Record<string, string>;
  busy: boolean;
  onRun: (slots: FlowSlots) => void;
  /** 段が進んだ・戻ったとき（途中でやめたときの記録に使う） */
  onStepChange: (stepIndex: number) => void;
}

function chipClass(selected: boolean): string {
  return `rounded-full border px-3.5 py-2 text-sm transition-colors ${
    selected
      ? 'border-primary-600 bg-primary-600 text-white'
      : 'border-text-200 text-text-800 hover:border-primary-300 hover:bg-primary-50'
  }`;
}

function EventStep({
  flow,
  events,
  ai,
  onPick,
}: {
  flow: ChatFlowButton;
  events: Map<string, ChatHomeEvent>;
  /** 選ぶと AI を呼ぶ（最後の段） */
  ai: boolean;
  onPick: (eventId: string) => void;
}) {
  const choices = flow.eligibleEvents ?? [];
  if (choices.length === 0) {
    return <p className="text-sm text-text-600">{flow.disabledReason ?? '選べるイベントがありません。'}</p>;
  }
  return (
    <ul className="space-y-2">
      {choices.map((choice) => {
        const info = events.get(choice.eventId);
        return (
          <li key={choice.eventId}>
            <button
              type="button"
              onClick={() => onPick(choice.eventId)}
              className="flex w-full flex-col items-start rounded-xl border border-text-200 px-3 py-2 text-left hover:border-primary-300 hover:bg-primary-50"
            >
              <span className="text-sm font-medium text-text-900">
                {info?.title ?? 'イベント'}
                {ai && (
                  <span className="ml-1">
                    <AiMark />
                  </span>
                )}
              </span>
              <span className="text-[11px] text-text-500">
                {/* 日時が決まっていないと日付と段階が同じ語（日程調整中・日時未定）になるので重ねない */}
                {[...new Set([info?.dateLabel, info?.phaseLabel, choice.hint].filter(Boolean))].join('・')}
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

function CommentStep({ eventId, ai, onPick }: { eventId: string; ai: boolean; onPick: (id: string) => void }) {
  const { user } = useAuth();
  const query = useQuery({
    queryKey: chatQueryKeys.comments(user?.id ?? '', eventId),
    queryFn: () => getChatComments(eventId),
    enabled: !!eventId,
  });
  if (query.isLoading) return <p className="text-sm text-text-500">コメントを読み込んでいます…</p>;
  const comments = query.data ?? [];
  if (comments.length === 0) return <p className="text-sm text-text-600">コメントがありません。</p>;
  return (
    <ul className="space-y-2">
      {comments.map((comment) => (
        <li key={comment.commentId}>
          <button
            type="button"
            onClick={() => onPick(comment.commentId)}
            className="flex w-full flex-col items-start rounded-xl border border-text-200 px-3 py-2 text-left hover:border-primary-300 hover:bg-primary-50"
          >
            <span className="text-[11px] text-text-500">
              {comment.author}
              {comment.looksLikeQuestion && <span className="ml-1.5 text-warning-700">質問</span>}
              {ai && (
                <span className="ml-1.5">
                  <AiMark />
                </span>
              )}
            </span>
            <span className="text-sm text-text-800 break-words">{comment.text}</span>
          </button>
        </li>
      ))}
    </ul>
  );
}

function NumberStep({ step, onSubmit }: { step: ChatFlowStep; onSubmit: (value: string) => void }) {
  const [value, setValue] = useState('');
  const number = Number(value.replace(/[,，]/g, ''));
  const min = step.minValue ?? 0;
  const max = step.maxValue ?? 10 ** 9;
  const reason = !value.trim()
    ? '金額を入れてください'
    : !Number.isInteger(number) || number < min || number > max
      ? `${min.toLocaleString()}〜${max.toLocaleString()} の数字で入れてください`
      : null;
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!reason) onSubmit(String(number));
      }}
      className="space-y-2"
    >
      <label htmlFor={`step-${step.id}`} className="sr-only">
        {step.label}
      </label>
      <input
        id={`step-${step.id}`}
        inputMode="numeric"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder="例: 30000"
        className="w-full rounded-xl border border-text-200 px-3 py-2 text-base focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400"
      />
      <button
        type="submit"
        disabled={!!reason}
        aria-describedby="number-step-reason"
        className="rounded-xl bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:bg-text-200 disabled:text-text-400"
      >
        計算する
      </button>
      <DisabledReason id="number-step-reason" reason={reason} align="left" />
    </form>
  );
}

export default function ChatFlowSteps({
  flow,
  events,
  initialSlots,
  busy,
  onRun,
  onStepChange,
}: ChatFlowStepsProps) {
  const [multi, setMulti] = useState<string[]>([]);
  // 決まっている段（イベントなど）は飛ばす
  const steps = (flow.steps ?? []).filter((step) => !(step.id in initialSlots));

  // イベントごとに選べる選択肢（サーバーのルールで絞った順）
  const optionsFor = (current: ChatFlowStep, values: FlowSlots) => {
    const id = typeof values.eventId === 'string' ? values.eventId : '';
    const order = flow.eligibleEvents?.find((choice) => choice.eventId === id)?.options?.[current.id];
    const all = current.options ?? [];
    if (!order) return all;
    return order
      .map((value) => all.find((option) => option.value === value))
      .filter((option): option is NonNullable<typeof option> => !!option);
  };

  // 選択肢が1つしか無い段は、押させずに埋める。ただし最後の段は埋めない（AI を呼ぶのは利用者が押したときだけ）
  const soleOption = (at: number, values: FlowSlots): string | null => {
    const current = steps[at];
    if (!current || current.kind !== CHAT_STEP_KIND.CHOICE || at >= steps.length - 1) return null;
    const choices = optionsFor(current, values);
    return choices.length === 1 ? choices[0].value : null;
  };

  const settle = (from: number, values: FlowSlots) => {
    let at = from;
    let next = values;
    for (let only = soleOption(at, next); only !== null; only = soleOption(at, next)) {
      next = { ...next, [steps[at].id]: only };
      at += 1;
    }
    return { at, next };
  };

  const [{ index, slots }, setPosition] = useState(() => {
    const start = settle(0, initialSlots);
    return { index: start.at, slots: start.next };
  });
  const step = steps[index];
  // この段で選ぶと AI を呼ぶ（AI を使う用途の最後の段）
  const aiNext = flow.usesModel && index === steps.length - 1;

  // 戻る先（埋めた段は飛ばす）。無ければ -1
  const previousIndex = (() => {
    for (let at = index - 1; at >= 0; at -= 1) {
      if (soleOption(at, slots) === null) return at;
    }
    return -1;
  })();

  // いまの段を親に知らせる（途中でやめた段の記録）。埋めた段を飛ばして 2 段目から始まることもある
  useEffect(() => {
    onStepChange(index);
  }, [index, onStepChange]);

  const advance = (stepId: string, value: string | string[]) => {
    const { at, next } = settle(index + 1, { ...slots, [stepId]: value });
    if (at >= steps.length) {
      setPosition({ index, slots: next });
      onRun(next);
    } else {
      setPosition({ index: at, slots: next });
    }
  };

  const back = () => {
    setPosition({ index: previousIndex, slots });
  };

  const eventId = typeof slots.eventId === 'string' ? slots.eventId : '';
  const eventInfo = eventId ? events.get(eventId) : undefined;
  const options = (current: ChatFlowStep) => optionsFor(current, slots);

  // ここまでに選んだ（埋めた）選択肢。何で作るのかを見えるようにする
  const chosen = steps
    .slice(0, index)
    .filter((done) => done.kind === CHAT_STEP_KIND.CHOICE)
    .map((done) => done.options?.find((option) => option.value === slots[done.id])?.label)
    .filter((label): label is string => !!label);

  return (
    <div className="space-y-3">
      <div className="flex min-h-7 items-center justify-between text-xs text-text-500">
        {/* 最初の段では、ヘッダーの戻る（ホームへ）と同じになるので出さない */}
        {previousIndex >= 0 ? (
          <button
            type="button"
            onClick={back}
            disabled={busy}
            className="flex items-center gap-0.5 rounded-lg py-1 pr-2 hover:text-text-800"
          >
            <ChevronLeftIcon className="h-4 w-4" />
            戻る
          </button>
        ) : (
          <span />
        )}
        {steps.length > 1 && (
          <span className="tabular-nums">
            {index + 1} / {steps.length}
          </span>
        )}
      </div>

      {(eventInfo || chosen.length > 0) && (
        <div className="flex flex-wrap gap-1.5">
          {eventInfo && (
            <span className="inline-flex max-w-full rounded-full bg-primary-50 px-3 py-1 text-xs font-medium text-primary-700">
              <span className="truncate">
                {eventInfo.title}（{eventInfo.dateLabel}）
              </span>
            </span>
          )}
          {chosen.map((label) => (
            <span
              key={label}
              className="inline-flex rounded-full bg-primary-50 px-3 py-1 text-xs font-medium text-primary-700"
            >
              {label}
            </span>
          ))}
        </div>
      )}

      {busy && <p className="text-sm text-text-500" aria-live="polite">準備しています…</p>}

      {!busy && !step && (
        <p className="text-sm text-text-600">うまく始められませんでした。戻ってもう一度選んでください。</p>
      )}

      {!busy && step && (
        <div className="space-y-2">
          <p className="text-sm font-medium text-text-900">{step.label}</p>
          {aiNext && (
            <p className="text-[11px] text-text-500">
              <AiMark /> 選ぶとAIが作ります（今月の利用量を使います）
            </p>
          )}
          {step.kind === CHAT_STEP_KIND.EVENT && (
            <EventStep flow={flow} events={events} ai={aiNext} onPick={(id) => advance(step.id, id)} />
          )}
          {step.kind === CHAT_STEP_KIND.CHOICE && (
            <div className="flex flex-wrap gap-2">
              {options(step).map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => advance(step.id, option.value)}
                  className={chipClass(false)}
                >
                  {option.label}
                  {aiNext && (
                    <span className="ml-1">
                      <AiMark />
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
          {step.kind === CHAT_STEP_KIND.MULTI && (
            <div className="space-y-2">
              <div className="flex flex-wrap gap-2">
                {options(step).map((option) => {
                  const selected = multi.includes(option.value);
                  return (
                    <button
                      key={option.value}
                      type="button"
                      aria-pressed={selected}
                      onClick={() =>
                        setMulti((current) =>
                          selected
                            ? current.filter((value) => value !== option.value)
                            : [...current, option.value],
                        )
                      }
                      className={chipClass(selected)}
                    >
                      {option.label}
                    </button>
                  );
                })}
              </div>
              <button
                type="button"
                onClick={() => advance(step.id, multi)}
                className="rounded-xl bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
              >
                {multi.length > 0 ? '次へ' : '特に無いので次へ'}
                {aiNext && (
                  <span className="ml-1">
                    <AiMark className="text-white" />
                  </span>
                )}
              </button>
            </div>
          )}
          {step.kind === CHAT_STEP_KIND.NUMBER && (
            <NumberStep step={step} onSubmit={(value) => advance(step.id, value)} />
          )}
          {step.kind === CHAT_STEP_KIND.COMMENT && (
            <CommentStep eventId={eventId} ai={aiNext} onPick={(id) => advance(step.id, id)} />
          )}
        </div>
      )}
    </div>
  );
}
